import type { Locale } from "@/lib/i18n";

/**
 * Server-side `fetch` in Node often prefers IPv6 for `localhost` (::1) while
 * `runserver` may only listen on IPv4 — use 127.0.0.1 for SSR when hostname is localhost.
 */
function normalizeApiBaseUrlForServer(base: string): string {
  if (typeof window !== "undefined") return base;
  try {
    const u = new URL(base);
    if (u.hostname === "localhost" || u.hostname === "::1") {
      u.hostname = "127.0.0.1";
    }
    return u.toString().replace(/\/+$/, "");
  } catch {
    return base.replace(/\/+$/, "");
  }
}

/** When visiting http://SERVER_IP:3030, the browser must call the API on the same host (not localhost). */
function inferBrowserApiBaseUrl(): string {
  if (typeof window === "undefined") return "";
  const { protocol, hostname } = window.location;
  const apiPort = (process.env.NEXT_PUBLIC_API_PORT || "8080").replace(/\/+$/, "");
  return `${protocol}//${hostname}:${apiPort}`;
}

function browserUsesSameOriginApiProxy(): boolean {
  if (typeof window === "undefined") return false;
  const relative = (process.env.NEXT_PUBLIC_API_RELATIVE || "").trim() === "1";
  const fromPublic = (process.env.NEXT_PUBLIC_API_BASE_URL || "").trim();
  return relative && !fromPublic;
}

/**
 * Base URL for API/media fetches. In the browser with `NEXT_PUBLIC_API_RELATIVE=1` and no
 * `NEXT_PUBLIC_API_BASE_URL`, returns "" so requests stay on the Next origin (rewrites proxy to Django).
 */
export function getApiBaseUrl(): string {
  const isServer = typeof window === "undefined";

  const fromPublic = (process.env.NEXT_PUBLIC_API_BASE_URL || "").trim();
  const fromServerOnly = (process.env.API_BASE_URL || "").trim();

  if (!isServer && browserUsesSameOriginApiProxy()) {
    return "";
  }

  let raw: string;
  if (isServer) {
    // Docker / SSR: prefer internal URL, then public, then default.
    raw = fromServerOnly || fromPublic || "http://127.0.0.1:8080";
  } else {
    // Browser: public env wins; if unset/empty, same host as the site (typical IP:PORT deploy).
    raw = fromPublic || inferBrowserApiBaseUrl() || "http://127.0.0.1:8080";
  }

  const trimmed = raw.replace(/\/+$/, "");
  return normalizeApiBaseUrlForServer(trimmed);
}

export type ApiFetchInit = Omit<RequestInit, "headers"> & {
  locale: Locale;
  headers?: HeadersInit;
};

export async function apiFetch<T>(path: string, init: ApiFetchInit): Promise<T> {
  const url = `${getApiBaseUrl()}${path.startsWith("/") ? "" : "/"}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: {
      Accept: "application/json",
      "X-Language": init.locale,
      ...(init.headers ?? {}),
    },
    // For public content, allow Next to cache with revalidation where used.
    // Individual pages can override with `cache: "no-store"` or `next: { revalidate }`.
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status} ${res.statusText}: ${text.slice(0, 500)}`);
  }

  return (await res.json()) as T;
}

